import { Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useEffect, useMemo } from 'react'
import { Palette } from 'src/feature/theme/model/Palette'
import { useMyTeamStore } from '../../store/my-team.store'
import { UsedSkillMetrics } from '../../model/my-team-skill-gaps'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded'
import DangerousRoundedIcon from '@mui/icons-material/DangerousRounded'

const COUNT_WIDTH = 12
const ICON_SIZE = 14

export interface TeamUsedSkillGapBoxProps {
  skillSet: UsedSkillMetrics
  onItemClick?: (itemName: string) => void
}

/**
 * Displays used skill with rating distribution counts.
 * Shows aggregated counts for rating and validation status.
 */
export const TeamUsedSkillGapBox = observer(({ skillSet, onItemClick }: TeamUsedSkillGapBoxProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()

  const pageTranslation = translation.pages.myTeam

  useEffect(() => {
    myTeamStore.getUsedSkillGaps(skillSet.setName, skillSet.ids)
  }, [skillSet.setName, skillSet.ids, myTeamStore])

  const skillGaps = myTeamStore.usedSkillGapsOverview?.[skillSet.setName]

  const countItems = useMemo(() => [
    { color: 'text.primary', count: skillGaps?.match || 0, icon: <CheckRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
    { color: Palette.DANGER, count: skillGaps?.gap || 0, icon: <ArrowDownwardRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
    { color: Palette.OLIVE_GREEN, count: skillGaps?.exceed || 0, icon: <ArrowUpwardRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
    { color: 'primary.main', count: skillGaps?.validated || 0, icon: <VerifiedUserRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
    { color: Palette.GOLDEN_ORANGE, count: skillGaps?.notValidated || 0, icon: <DangerousRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
  ], [skillGaps])

  return (
    <AppPaper
      sx={{
        ...styles.root,
        cursor: onItemClick ? 'pointer' : 'default',
        backgroundColor: 'background.dark',
        '&:hover': onItemClick ? {
          backgroundColor: 'action.hover',
        } : {},
      }}
      onClick={() => onItemClick?.(skillSet.setName)}
    >
      <AppBox sx={styles.container}>
        <AppBox sx={styles.skillName}>
          {(skillSet.ids ?? []).length > 1 && (
            <Tooltip title={pageTranslation.metricsHeader.sharedSkillHint()} placement='top' arrow>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'secondary.main' }} />
            </Tooltip>
          )}
          <Tooltip title={skillSet.setName} placement='top' arrow>
            <Typography fontSize={12} noWrap>
              {skillSet.setName}
            </Typography>
          </Tooltip>
        </AppBox>
        <AppBox sx={styles.countsWrapper}>
          {countItems.map(({ color, count, icon }) => (
            <AppBox key={color} sx={styles.countItem}>
              <AppBox sx={{ ...styles.iconBox, color }}>{icon}</AppBox>
              <Typography sx={styles.countText}>{count}</Typography>
            </AppBox>
          ))}
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    width: '100%',
    flexDirection: 'column',
    padding: '2px 6px',
    borderRadius: '10px',
  },
  container: {
    width: '100%',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  skillName: {
    flexDirection: 'row',
    justifyContent: { xs: 'center', md: 'flex-start' },
    alignItems: 'center',
    gap: 5,
  },
  countsWrapper: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  countItem: {
    gap: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: {
    fontSize: 14,
    minWidth: COUNT_WIDTH,
    textAlign: 'center',
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}
